// ============================================
// Admin Auth Context
//
// Provides admin authentication state to the admin panel.
// Listens to Firebase onAuthStateChanged, then checks
// admin_users/{uid} for role-based access.
// ============================================

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { User } from 'firebase/auth';
import {
  signInAdmin,
  signOutAdmin,
  checkAdminRole,
  onAdminAuthStateChanged,
  type AdminRole,
  type AdminUser,
} from '../services/firestore/adminAuthService';

type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated' | 'access_denied';

interface AdminAuthContextValue {
  /** Firebase Auth user (null if not signed in) */
  user: User | null;
  /** Admin role from admin_users collection */
  role: AdminRole | null;
  /** Admin display name */
  name: string | null;
  /** Current auth status */
  status: AuthStatus;
  /** Convenience: true while checking auth state */
  isLoading: boolean;
  /** Sign in with email + password */
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign out */
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [adminData, setAdminData] = useState<AdminUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  // Listen for Firebase Auth state changes
  useEffect(() => {
    const unsub = onAdminAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser || firebaseUser.isAnonymous) {
        // Not signed in or anonymous — treat as unauthenticated
        setUser(null);
        setAdminData(null);
        setStatus('unauthenticated');
        return;
      }

      // Signed in with email/password — check admin_users collection
      setUser(firebaseUser);
      setStatus('loading');

      try {
        const admin = await checkAdminRole(firebaseUser.uid);
        if (admin) {
          setAdminData(admin);
          setStatus('authenticated');
        } else {
          setAdminData(null);
          setStatus('access_denied');
        }
      } catch (err) {
        console.error('[AdminAuth] Role check failed:', err);
        setAdminData(null);
        setStatus('access_denied');
      }
    });

    return unsub;
  }, []);

  const handleSignIn = useCallback(async (email: string, password: string) => {
    setStatus('loading');
    try {
      const firebaseUser = await signInAdmin(email, password);
      setUser(firebaseUser);

      const admin = await checkAdminRole(firebaseUser.uid);
      if (admin) {
        setAdminData(admin);
        setStatus('authenticated');
      } else {
        setAdminData(null);
        setStatus('access_denied');
      }
    } catch (err) {
      setStatus('unauthenticated');
      throw err;
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOutAdmin();
    setUser(null);
    setAdminData(null);
    setStatus('unauthenticated');
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        role: adminData?.role ?? null,
        name: adminData?.name ?? null,
        status,
        isLoading: status === 'loading',
        signIn: handleSignIn,
        signOut: handleSignOut,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
